import { useColorMode, Badge, Flex, Text } from '@chakra-ui/react';
import {
  groupBadgeStyles,
  groupStyles,
} from '@texas/components/widgets/shared/styles';
import {
  SelectBorderStyle,
  reactSelectStyle,
  reactSelectTheme,
} from '@texas/resources/styles';
import { ReactSelectOption, ReactSelectOptionWithBadges } from '@texas/types';
import Select, { GroupBase, Props, components } from 'react-select';

export interface TexasSelectProps<
  ReactSelectType,
  IsMulti extends boolean = false,
> extends Props<ReactSelectType, IsMulti, GroupBase<ReactSelectType>> {
  borderRadius?: number | string;
  borderStyle?: SelectBorderStyle;
  colorMode?: 'light' | 'dark';
  isAngularTheme?: boolean;
  minHeight?: number | string;
  showBadges?: boolean;
  usePortal?: boolean;
}

const BadgeOption = (props: any) => {
  const option = props.data as ReactSelectOptionWithBadges;
  const hasBadges = option.badge1 ?? option.badge2 ?? option.badge3;

  return (
    <components.Option {...props}>
      <Flex align="center" gap={2} width="100%">
        <Text flex={1}>{option.label}</Text>
        {hasBadges && (
          <Flex gap={1}>
            {option.badge1?.visible && (
              <Badge fontSize="xs" variant="solid">
                {option.badge1.label}
              </Badge>
            )}
            {option.badge2?.visible && (
              <Badge fontSize="xs" variant="solid">
                {option.badge2.label}
              </Badge>
            )}
            {option.badge3?.visible && (
              <Badge fontSize="xs" variant="solid">
                {option.badge3.label}
              </Badge>
            )}
          </Flex>
        )}
      </Flex>
    </components.Option>
  );
};

export function TexasSelect<ReactSelectType, IsMulti extends boolean = false>(
  props: TexasSelectProps<ReactSelectType, IsMulti>,
) {
  const { colorMode: activeColorMode } = useColorMode();
  const {
    borderRadius,
    colorMode,
    minHeight,
    showBadges,
    usePortal,
    ...restProps
  } = props;
  const resolvedColorMode = colorMode ?? activeColorMode;

  return (
    <Select
      {...restProps}
      components={showBadges ? { Option: BadgeOption } : undefined}
      menuPortalTarget={usePortal ? document.body : restProps.menuPortalTarget}
      menuPosition={usePortal ? 'fixed' : restProps.menuPosition}
      styles={{
        ...restProps.styles,
        ...reactSelectStyle<IsMulti, ReactSelectType>({
          borderRadius,
          minHeight,
          mode: resolvedColorMode,
        }),
        ...(usePortal
          ? {
              menuPortal: (base) => ({ ...base, zIndex: 1401 }),
            }
          : null),
      }}
      theme={(theme) => reactSelectTheme(theme, resolvedColorMode)}
    />
  );
}

export const formatGroupLabel = (data: GroupBase<ReactSelectOption>) => (
  <div style={groupStyles}>
    <span>{data.label}</span>
    <span style={groupBadgeStyles}>{data.options.length}</span>
  </div>
);
